import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Search, ShoppingBag, UserRound, Menu, X, ChevronRight, ChevronLeft, ChevronDown, Star, Plus, Minus, Package, Truck,
  CheckCircle2, ArrowLeft, Heart, LogOut, MessageCircle, Send, Sparkles, Share2, Archive,
  Undo2, ImagePlus, Settings2, Tags, Trash2, Ticket, Upload, ZoomIn, Pencil, BarChart3, Mail,
} from "lucide-react";
import "@/App.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const LOGO = "https://customer-assets-0z36b82j.emergentagent.net/job_a00021b6-6b36-401d-a16f-0b76ea1aecf7/artifacts/79s8jc10_Logo.jpeg";
const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1629442123298-d9afc2180e6a?auto=format&fit=crop&w=900&q=85";
const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
const ORDER_STATUSES = ["Order placed", "Packed", "Shipped", "Delivered"];
const UNPAID = ["awaiting_payment", "payment_cancelled"];
const STATUS_LABELS = { awaiting_payment: "Unpaid · checkout open", payment_cancelled: "Unpaid · cancelled" };

const imageUrl = (image) => {
  if (!image) return DEFAULT_IMAGE;
  if (image.startsWith("http") || image.startsWith("data:") || image.startsWith("blob:")) return image;
  return `${API}/files/${image}`;
};

const isAdminPath = () => typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

// Reject expired/malformed tokens before we ever render the dashboard.
function loadValidToken() {
  const raw = sessionStorage.getItem("shreya_supplier_token") || "";
  if (!raw) return "";
  try {
    const payload = raw.split(".")[1];
    const pad = "=".repeat((-payload.length) % 4);
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/") + pad));
    if (!claims.exp || claims.exp * 1000 < Date.now()) throw new Error("expired");
    return raw;
  } catch {
    sessionStorage.removeItem("shreya_supplier_token");
    return "";
  }
}

// Module-level 401 interceptor — registers once, before any component mounts.
let __signOut = null;
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && sessionStorage.getItem("shreya_supplier_token")) {
      sessionStorage.removeItem("shreya_supplier_token");
      if (__signOut) __signOut();
    }
    return Promise.reject(error);
  }
);

function App() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState({ shipping_bar_text: "", free_shipping_threshold: 1999 });
  const [cart, setCart] = useState([]);
  const [page, setPage] = useState(isAdminPath() ? "dashboard" : "shop");
  const [supplierToken, setSupplierToken] = useState(loadValidToken());
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [sort, setSort] = useState("new_desc");
  const [categoryMode, setCategoryMode] = useState(false); // when true, hide hero & category chooser (dedicated category page)
  const [mobileMenu, setMobileMenu] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount, type, value}
  const [reviewingOrder, setReviewingOrder] = useState(null); // order dict for review modal

  const notify = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2600);
  }, []);

  // Anonymous visitor id persisted in localStorage. One id per browser profile.
  // Used only to dedupe daily unique-visitor counts; no personal data stored.
  const [visitorId] = useState(() => {
    try {
      let id = window.localStorage.getItem("rs_visitor_id");
      if (!id) {
        id = "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        window.localStorage.setItem("rs_visitor_id", id);
      }
      return id;
    } catch { return "v-anon"; }
  });

  // Fire a visit ping when the storefront page changes. Skips the admin dashboard.
  useEffect(() => {
    if (page === "dashboard" || page === "login") return;
    const productId = page === "product" ? (selected?.id || null) : null;
    axios.post(`${API}/visits`, {
      visitor_id: visitorId,
      path: `/${page}`,
      product_id: productId,
    }).catch(() => {});
  }, [page, selected?.id, visitorId]);

  // Wire the module-level 401 interceptor to this App instance so mid-session expiries
  // sign the supplier out and show a friendly toast.
  useEffect(() => {
    __signOut = () => {
      setSupplierToken("");
      notify("Session expired. Please sign in again.");
    };
    return () => { __signOut = null; };
  }, [notify]);

  const loadProducts = useCallback(async () => {
    const res = await axios.get(`${API}/products`, {
      params: {
        category: category === "All" ? undefined : category,
        search: search || undefined,
        page: pageNum,
        limit: 16,
        sort,
      },
    });
    const payload = res.data || {};
    // Backwards-compat: if server ever returns a plain list, wrap it.
    if (Array.isArray(payload)) {
      setProducts(payload);
      setTotalPages(1);
      setTotalProducts(payload.length);
    } else {
      setProducts(payload.items || []);
      setTotalPages(payload.pages || 1);
      setTotalProducts(payload.total || 0);
    }
  }, [category, search, pageNum, sort]);

  const loadCategories = useCallback(async () => {
    const res = await axios.get(`${API}/categories`);
    setCategories(["All", ...res.data]);
  }, []);

  const loadBanners = useCallback(async () => {
    const res = await axios.get(`${API}/banners`);
    setBanners(res.data);
  }, []);

  const loadSettings = useCallback(async () => {
    const res = await axios.get(`${API}/settings`);
    setSettings(res.data);
  }, []);

  useEffect(() => {
    setSearching(true);
    loadProducts()
      .catch(() => notify("Could not load the catalogue"))
      .finally(() => setSearching(false));
    if (search) {
      setTimeout(() => document.getElementById("catalogue")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [category, search, pageNum, sort, loadProducts, notify]);

  // Reset to page 1 whenever the filter/search/sort changes
  useEffect(() => { setPageNum(1); }, [category, search, sort]);

  // Debounce live search: fire actual search 350ms after typing stops
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Read ?cat= from URL to switch into dedicated category page mode
  useEffect(() => {
    const readCatFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get("cat");
      if (cat) {
        setCategory(cat);
        setCategoryMode(true);
        setPage("shop");
      } else {
        setCategoryMode(false);
      }
    };
    readCatFromUrl();
    window.addEventListener("popstate", readCatFromUrl);
    return () => window.removeEventListener("popstate", readCatFromUrl);
  }, []);

  useEffect(() => {
    loadCategories().catch(() => {});
    loadBanners().catch(() => {});
    loadSettings().catch(() => {});
  }, [loadCategories, loadBanners, loadSettings]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("product");
    if (pid) {
      const match = products.find((p) => p.id === pid);
      if (match) {
        setSelected(match);
        setPage("product");
      } else {
        // Not in current page; fetch single product directly
        axios.get(`${API}/products/${pid}`).then((res) => {
          if (res.data) {
            setSelected(res.data);
            setPage("product");
          }
        }).catch(() => {});
      }
    }
  }, [products]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput.trim());
  };

  const cartKey = (product, variant) => `${product.id}::${variant?.id || ""}`;
  const addToCart = (product, variant = null, quantity = 1) => {
    const price = variant?.price ?? product.price;
    const parts = [];
    if (variant?.color) parts.push(variant.color);
    if (variant?.size) parts.push(variant.size);
    const variantLabel = parts.join(" · ") || null;
    const key = cartKey(product, variant);
    setCart((current) => {
      const found = current.find((item) => item.key === key);
      return found
        ? current.map((item) => (item.key === key ? { ...item, quantity: item.quantity + quantity } : item))
        : [
            ...current,
            {
              ...product,
              key,
              price,
              quantity,
              variant_id: variant?.id || null,
              variant_label: variantLabel,
              color: variant?.color || null,
              size: variant?.size || null,
            },
          ];
    });
    setAppliedCoupon(null);
    notify(`${product.name}${variantLabel ? ` (${variantLabel})` : ""} added to your bag`);
  };
  const updateQuantity = (key, amount) => {
    setCart((current) =>
      current
        .map((item) => (item.key === key ? { ...item, quantity: Math.max(0, item.quantity + amount) } : item))
        .filter((item) => item.quantity)
    );
    setAppliedCoupon(null);
  };
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const applyCoupon = async (code) => {
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code, subtotal: total });
      setAppliedCoupon(res.data);
      notify(`Coupon ${res.data.code} applied · ₹${res.data.discount} off`);
    } catch (err) {
      setAppliedCoupon(null);
      notify(err.response?.data?.detail || "That code isn't valid");
    }
  };

  const go = (next) => {
    setPage(next);
    setSelected(null);
    setMobileMenu(false);
    setCategoriesOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const openProduct = (product) => {
    setSelected(product);
    setPage("product");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const loadOrders = async (email) => {
    if (!email) { setOrders([]); return; }
    const res = await axios.get(`${API}/orders`, { params: { email } });
    setOrders(res.data);
  };

  if (isAdminPath()) {
    return (
      <div className="app-shell admin-shell">
        {supplierToken ? (
          <Dashboard
            categories={categories}
            loadProducts={loadProducts}
            loadCategories={loadCategories}
            loadBanners={loadBanners}
            loadSettings={loadSettings}
            notify={notify}
            token={supplierToken}
            settings={settings}
            onLogout={() => {
              sessionStorage.removeItem("shreya_supplier_token");
              setSupplierToken("");
            }}
          />
        ) : (
          <SupplierLogin
            setSupplierToken={(token) => {
              sessionStorage.setItem("shreya_supplier_token", token);
              setSupplierToken(token);
            }}
          />
        )}
        {toast && <div className="toast" data-testid="toast-message">{toast}</div>}
      </div>
    );
  }

  const freeShippingThreshold = settings.free_shipping_threshold || 1999;

  return (
    <div className="app-shell">
      <div className="announcement" data-testid="announcement-bar">
        {settings.shipping_bar_text || "Free shipping on orders above ₹1999 · Carefully packed from Shreya’s studio"}
      </div>
      <header className="site-header" data-testid="store-header">
        <button className="icon-button mobile-only" data-testid="mobile-menu-button" onClick={() => { setMobileMenu(!mobileMenu); setCategoriesOpen(false); }} aria-label="Open menu">
          {mobileMenu ? <X /> : <Menu />}
        </button>
        <button className="brand" data-testid="brand-home-button" onClick={() => go("shop")}>
          <img src={LOGO} alt="Resin Supplier Shreya logo" />
        </button>
        <nav className={mobileMenu ? "main-nav open" : "main-nav"} data-testid="main-navigation">
          <button data-testid="nav-shop-button" className={page === "shop" && !categoryMode ? "active" : ""} onClick={() => {
            setCategory("All");
            setCategoryMode(false);
            if (window.location.search) window.history.pushState({}, "", window.location.pathname);
            go("shop");
          }}>Shop</button>
          <div
            className={categoriesOpen ? "nav-dropdown open" : "nav-dropdown"}
            data-testid="nav-categories-dropdown"
            onMouseEnter={() => { if (!mobileMenu && window.matchMedia("(hover: hover)").matches) setCategoriesOpen(true); }}
            onMouseLeave={() => { if (!mobileMenu && window.matchMedia("(hover: hover)").matches) setCategoriesOpen(false); }}
          >
            <button
              className="nav-trigger"
              data-testid="nav-categories-trigger"
              onClick={() => setCategoriesOpen((o) => !o)}
              aria-expanded={categoriesOpen}
            >
              Categories <ChevronDown size={13} />
            </button>
            <div className="nav-menu" role="menu">
              {(() => {
                const all = categories.filter((c) => c !== "All");
                const allowed = (settings.menu_categories && settings.menu_categories.length) ? settings.menu_categories : all;
                const shown = all.filter((c) => allowed.includes(c));
                if (!shown.length) return <span className="nav-empty">No categories in menu</span>;
                return shown.map((cat) => (
                  <button
                    key={cat}
                    role="menuitem"
                    data-testid={`nav-cat-${cat.toLowerCase().replace(/\s+/g, "-")}-button`}
                    onClick={() => {
                      setCategory(cat);
                      setCategoryMode(true);
                      setCategoriesOpen(false);
                      setMobileMenu(false);
                      go("shop");
                      const url = `${window.location.pathname}?cat=${encodeURIComponent(cat)}`;
                      window.history.pushState({}, "", url);
                    }}
                  >
                    {cat}
                  </button>
                ));
              })()}
            </div>
          </div>
          <button data-testid="nav-orders-button" onClick={() => go("orders")}>Track order</button>
        </nav>
        <div className="header-actions">
          <form className="search-wrap" onSubmit={submitSearch} role="search">
            <Search size={18} />
            <input
              data-testid="product-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search materials"
              aria-label="Search materials"
            />
            <button type="submit" data-testid="product-search-submit" aria-label="Search" className="search-submit">
              <ChevronRight size={16} />
            </button>
          </form>
          <button className="icon-button" data-testid="account-button" onClick={() => go("orders")} aria-label="My orders">
            <UserRound />
          </button>
          <button className="bag-button" data-testid="cart-button" onClick={() => go("cart")}>
            <ShoppingBag />
            <span data-testid="cart-count">{cart.reduce((n, item) => n + item.quantity, 0)}</span>
          </button>
        </div>
      </header>

      {page === "shop" && (
        <Shop
          products={products}
          banners={banners}
          categories={categories}
          category={category}
          setCategory={setCategory}
          addToCart={addToCart}
          openProduct={openProduct}
          searching={searching}
          settings={settings}
          pageNum={pageNum}
          setPageNum={setPageNum}
          totalPages={totalPages}
          totalProducts={totalProducts}
          sort={sort}
          setSort={setSort}
          categoryMode={categoryMode}
          exitCategoryMode={() => {
            setCategoryMode(false);
            setCategory("All");
            if (window.location.search) window.history.pushState({}, "", window.location.pathname);
          }}
        />
      )}
      {page === "product" && selected && <ProductDetail product={selected} addToCart={addToCart} go={go} />}
      {page === "cart" && (
        <Cart
          cart={cart}
          updateQuantity={updateQuantity}
          total={total}
          go={go}
          threshold={freeShippingThreshold}
          flatShipping={settings.flat_shipping || 80}
          appliedCoupon={appliedCoupon}
          setAppliedCoupon={setAppliedCoupon}
          applyCoupon={applyCoupon}
        />
      )}
      {page === "checkout" && (
        <Checkout
          cart={cart}
          total={total}
          threshold={freeShippingThreshold}
          flatShipping={settings.flat_shipping || 80}
          go={go}
          setCustomer={setCustomer}
          notify={notify}
          appliedCoupon={appliedCoupon}
        />
      )}
      {page === "orders" && (
        <Orders
          customer={customer}
          orders={orders}
          loadOrders={loadOrders}
          go={go}
          onReview={setReviewingOrder}
        />
      )}
      {reviewingOrder && (
        <ReviewModal
          order={reviewingOrder}
          onClose={() => setReviewingOrder(null)}
          notify={notify}
        />
      )}
      {toast && <div className="toast" data-testid="toast-message">{toast}</div>}
      <AIChat />
      <footer>
        <div className="footer-inner">
          <div className="footer-brand">
            <img src={LOGO} alt="Resin Supplier Shreya" />
            <p>Small-batch materials for big creative ideas.</p>
          </div>
          <div className="footer-contact" data-testid="footer-contact">
            <strong>Contact us</strong>
            <a href="mailto:shreyamehta23290@gmail.com" data-testid="footer-email">shreyamehta23290@gmail.com</a>
            <a href="tel:+917984484141" data-testid="footer-phone">+91 79844 84141</a>
          </div>
          <div className="footer-payments" data-testid="footer-payments">
            <strong>Secure payments</strong>
            <div className="razorpay-badge">
              <small>Payment processing partner</small>
              <b>Razorpay</b>
              <div className="pay-brands">
                <span>Visa</span>
                <span>UPI</span>
                <span>RuPay</span>
                <span>Mastercard</span>
                <span>Net banking</span>
              </div>
            </div>
          </div>
          <span className="footer-copyright" data-testid="footer-copyright">© 2026 Resin Supplier Shreya · Surat, India</span>
        </div>
      </footer>
    </div>
  );
}

function HeroSlider({ slides }) {
  const [idx, setIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 760px)").matches);
  const total = slides.length;
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 760px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  useEffect(() => {
    if (total <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 5500);
    return () => clearInterval(t);
  }, [total]);
  if (!total) {
    return (
      <section className="hero-slider empty" data-testid="hero-slider-empty">
        <div className="empty-slide">
          <p className="eyebrow">MATERIALS THAT MAKE MAGIC</p>
          <h1>Pour something <em>beautiful.</em></h1>
          <p>Premium resin supplies, handpicked for makers who care about every detail.</p>
          <button
            className="pink-button"
            data-testid="hero-empty-shop-button"
            onClick={() => document.getElementById("catalogue").scrollIntoView({ behavior: "smooth" })}
          >
            Explore the collection <ChevronRight size={17} />
          </button>
        </div>
      </section>
    );
  }
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);
  return (
    <section className="hero-slider" data-testid="hero-slider">
      {slides.map((s, i) => {
        const cta = s.cta_label;
        const hasCopy = !!(s.title || s.subtitle || cta);
        const img = (isMobile && s.image_mobile) ? s.image_mobile : s.image;
        const inner = (
          <>
            <img src={imageUrl(img)} alt={s.title || `Slide ${i + 1}`} />
            {hasCopy && (
              <div className="slide-overlay">
                <div className="slide-copy">
                  {s.subtitle && <p className="eyebrow">{s.subtitle}</p>}
                  {s.title && <h1>{s.title}</h1>}
                  {cta && (
                    <span className="pink-button" data-testid={`hero-slide-cta-${i}`}>
                      {cta} <ChevronRight size={17} />
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        );
        const commonProps = {
          className: i === idx ? "slide active" : "slide",
          "data-testid": `hero-slide-${i}`,
          "aria-hidden": i !== idx,
        };
        if (s.link) {
          return (
            <a
              key={s.id}
              href={s.link}
              {...commonProps}
              onClick={(e) => {
                if (s.link.startsWith("#")) {
                  e.preventDefault();
                  document.getElementById(s.link.slice(1))?.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              {inner}
            </a>
          );
        }
        return <div key={s.id} {...commonProps}>{inner}</div>;
      })}
      {total > 1 && (
        <>
          <button className="slider-arrow prev" onClick={prev} aria-label="Previous slide" data-testid="hero-slider-prev"><ChevronLeft size={22} /></button>
          <button className="slider-arrow next" onClick={next} aria-label="Next slide" data-testid="hero-slider-next"><ChevronRight size={22} /></button>
          <div className="slider-dots" data-testid="hero-slider-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={i === idx ? "dot active" : "dot"}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-slider-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function Shop({ products, banners, categories, category, setCategory, addToCart, openProduct, searching, settings, pageNum, setPageNum, totalPages, totalProducts, sort, setSort, categoryMode, exitCategoryMode }) {
  const eyebrow = (settings && settings.catalog_eyebrow) || "SHOP THE STUDIO";
  const heading = (settings && settings.catalog_heading) || "Find your next favourite.";
  const chipList = (() => {
    const rest = categories.filter((c) => c !== "All");
    const allowed = (settings && settings.catalog_categories && settings.catalog_categories.length)
      ? settings.catalog_categories
      : rest;
    return ["All", ...rest.filter((c) => allowed.includes(c))];
  })();

  const pageWindow = (() => {
    // Build a compact page window like: 1 … 4 5 [6] 7 8 … 20
    const list = [];
    const push = (v) => { if (!list.includes(v)) list.push(v); };
    push(1);
    for (let i = pageNum - 2; i <= pageNum + 2; i++) {
      if (i >= 2 && i <= totalPages - 1) push(i);
    }
    if (totalPages > 1) push(totalPages);
    return list;
  })();

  return (
    <main data-testid="shop-page">
      {!categoryMode && <HeroSlider slides={banners} />}

      {!categoryMode && (
        <section className="trust-strip">
          <span><CheckCircle2 /> Quality checked</span>
          <span><Truck /> Pan-India delivery</span>
          <span><Heart /> Maker-loved</span>
        </section>
      )}

      <section id="catalogue" className={categoryMode ? "catalogue category-mode" : "catalogue"}>
        <div className="section-heading">
          <div>
            {categoryMode ? (
              <>
                <button className="back-link" data-testid="exit-category-button" onClick={exitCategoryMode}>
                  <ArrowLeft size={14} /> All materials
                </button>
                <h1 data-testid="category-page-heading">{category}</h1>
              </>
            ) : (
              <>
                <p className="eyebrow" data-testid="catalog-eyebrow">{eyebrow}</p>
                <h2 data-testid="catalog-heading">{heading}</h2>
              </>
            )}
          </div>
          <span data-testid="product-count">{searching ? "Loading…" : `${totalProducts} materials`}</span>
        </div>

        {!categoryMode && (
          <div className="category-row" data-testid="category-filters">
            {chipList.map((item) => (
              <button
                data-testid={`category-${item.toLowerCase().replace(/\s+/g, "-")}-button`}
                className={category === item ? "selected" : ""}
                onClick={() => setCategory(item)}
                key={item}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        <div className="sort-row" data-testid="sort-row">
          <label htmlFor="sort-select">Sort by</label>
          <select
            id="sort-select"
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="new_desc">Newest first</option>
            <option value="new_asc">Oldest first</option>
            <option value="name_asc">Name: A to Z</option>
            <option value="name_desc">Name: Z to A</option>
            <option value="price_asc">Price: Low to high</option>
            <option value="price_desc">Price: High to low</option>
            <option value="bestselling">Best selling</option>
          </select>
        </div>

        <div className="product-grid">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} addToCart={addToCart} openProduct={openProduct} />
          ))}
        </div>
        {!products.length && !searching && (
          <div className="empty-state" data-testid="empty-catalogue">
            No materials found. Try another search.
          </div>
        )}

        {totalPages > 1 && (
          <nav className="pagination" data-testid="pagination">
            <button
              className="page-btn"
              onClick={() => setPageNum(Math.max(1, pageNum - 1))}
              disabled={pageNum === 1}
              data-testid="page-prev"
              aria-label="Previous page"
            ><ChevronLeft size={14} /> Prev</button>
            {pageWindow.map((p, idx) => {
              const prev = idx > 0 ? pageWindow[idx - 1] : 0;
              const showGap = prev && p - prev > 1;
              return (
                <span key={p} style={{ display: "inline-flex", gap: 4 }}>
                  {showGap && <span className="page-gap">…</span>}
                  <button
                    className={p === pageNum ? "page-btn active" : "page-btn"}
                    onClick={() => setPageNum(p)}
                    data-testid={`page-${p}`}
                  >{p}</button>
                </span>
              );
            })}
            <button
              className="page-btn"
              onClick={() => setPageNum(Math.min(totalPages, pageNum + 1))}
              disabled={pageNum === totalPages}
              data-testid="page-next"
              aria-label="Next page"
            >Next <ChevronRight size={14} /></button>
          </nav>
        )}
      </section>
    </main>
  );
}

function ProductCard({ product, addToCart, openProduct }) {
  return (
    <article className="product-card" data-testid={`product-card-${product.id}`}>
      <button className="product-image" data-testid={`product-view-${product.id}`} onClick={() => openProduct(product)}>
        <img src={imageUrl(product.image)} alt={product.name} />
      </button>
      <div className="product-info">
        <div className="rating">
          {product.reviews > 0 ? (<><Star size={13} fill="currentColor" /> {product.rating} <span>({product.reviews})</span></>) : (<span>New</span>)}
        </div>
        <button className="product-name" data-testid={`product-name-${product.id}`} onClick={() => openProduct(product)}>
          {product.name}
        </button>
        <p>{product.unit}</p>
        <div className="product-bottom">
          <strong>{money(product.price)}</strong>
          <button className="add-button" data-testid={`add-product-${product.id}-button`} onClick={() => addToCart(product)}>
            <Plus size={17} /> Add
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductDetail({ product, addToCart, go }) {
  const baseGallery = (product.images && product.images.length > 0) ? product.images : [product.image];
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [reviews, setReviews] = useState([]);

  const variants = product.variants || [];
  const variantImages = product.variant_images || {};
  const variantColors = useMemo(() => {
    const list = variants.map((v) => v.color).filter(Boolean);
    return Array.from(new Set([...(product.colors || []), ...list]));
  }, [product, variants]);
  const variantSizes = useMemo(() => {
    const list = variants.map((v) => v.size).filter(Boolean);
    return Array.from(new Set([...(product.sizes || []), ...list]));
  }, [product, variants]);
  const hasVariantMatrix = variants.length > 0;
  const [selectedColor, setSelectedColor] = useState(variantColors[0] || null);
  const [selectedSize, setSelectedSize] = useState(variantSizes[0] || null);

  // Gallery swaps to the selected color's photos when available
  const gallery = useMemo(() => {
    if (selectedColor && variantImages[selectedColor] && variantImages[selectedColor].length) {
      return variantImages[selectedColor];
    }
    return baseGallery;
  }, [selectedColor, variantImages, baseGallery]);

  useEffect(() => {
    setSelectedColor(variantColors[0] || null);
    setSelectedSize(variantSizes[0] || null);
  }, [product.id]); // reset on product change

  useEffect(() => {
    setActive(0); // reset thumb when color-gallery swaps
  }, [selectedColor]);

  const matchedVariant = useMemo(() => {
    if (!hasVariantMatrix) {
      if (!selectedColor && !selectedSize) return null;
      // synthesize a lightweight variant from bare colors/sizes lists
      return { id: `${selectedColor || ""}-${selectedSize || ""}`, color: selectedColor, size: selectedSize, price: product.price, stock: product.stock };
    }
    return variants.find((v) => (v.color || null) === (selectedColor || null) && (v.size || null) === (selectedSize || null)) || null;
  }, [variants, selectedColor, selectedSize, hasVariantMatrix, product.price, product.stock]);

  const displayPrice = matchedVariant?.price ?? product.price;
  const outOfStock = matchedVariant ? matchedVariant.stock <= 0 : false;
  const needsSelection = (variantColors.length > 0 && !selectedColor) || (variantSizes.length > 0 && !selectedSize);

  const swatchStyle = (color) => {
    const named = (color || "").toLowerCase();
    return { backgroundColor: named || "#f4d3dc" };
  };

  useEffect(() => {
    setActive(0);
    axios.get(`${API}/reviews`, { params: { product_id: product.id } })
      .then((res) => setReviews(res.data))
      .catch(() => setReviews([]));
  }, [product.id]);

  return (
    <main className="detail-page" data-testid="product-detail-page">
      <button className="back-link" data-testid="back-to-shop-button" onClick={() => go("shop")}>
        <ArrowLeft size={16} /> Back to shop
      </button>
      <div className="detail-layout">
        <div className="gallery">
          <button
            className="gallery-main"
            data-testid="gallery-main-image"
            onClick={() => setZoom(true)}
            aria-label="Zoom image"
          >
            <img src={imageUrl(gallery[active])} alt={product.name} />
            <span className="zoom-badge"><ZoomIn size={14} /> Tap to zoom</span>
          </button>
          {gallery.length > 1 && (
            <div className="gallery-thumbs" data-testid="gallery-thumbs">
              {gallery.map((img, idx) => (
                <button
                  key={img + idx}
                  className={idx === active ? "thumb active" : "thumb"}
                  data-testid={`gallery-thumb-${idx}`}
                  onClick={() => setActive(idx)}
                  aria-label={`View photo ${idx + 1}`}
                >
                  <img src={imageUrl(img)} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="detail-copy">
          <p className="eyebrow">{product.category.toUpperCase()}</p>
          <h1>{product.name}</h1>
          <div className="rating large">
            {product.reviews > 0 ? (<><Star size={16} fill="currentColor" /> {product.rating} <span>({product.reviews} maker reviews)</span></>) : (<span>No reviews yet</span>)}
          </div>
          <p className="detail-description">{product.description}</p>
          <div className="price-line">
            <strong>{money(displayPrice)}</strong>
            <span>per {product.unit}</span>
          </div>
          {variantColors.length > 0 && (
            <div className="variant-block" data-testid="variant-colors">
              <p className="variant-label">Color{selectedColor ? `: ${selectedColor}` : ""}</p>
              <div className="swatch-row">
                {variantColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`swatch ${selectedColor === c ? "selected" : ""}`}
                    style={swatchStyle(c)}
                    aria-label={c}
                    title={c}
                    onClick={() => setSelectedColor(c)}
                    data-testid={`swatch-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ))}
              </div>
            </div>
          )}
          {variantSizes.length > 0 && (
            <div className="variant-block" data-testid="variant-sizes">
              <p className="variant-label">{(product.size_label || "Size")}{selectedSize ? `: ${selectedSize}` : ""}</p>
              <div className="size-row">
                {variantSizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`size-pill ${selectedSize === s ? "selected" : ""}`}
                    onClick={() => setSelectedSize(s)}
                    data-testid={`size-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          <div className="detail-meta">
            <span><CheckCircle2 /> {outOfStock ? "Out of stock" : "In stock"}</span>
            <span><Truck /> Ships in 1–2 days</span>
          </div>
          <button
            className="pink-button wide"
            data-testid="detail-add-to-cart-button"
            disabled={outOfStock || needsSelection}
            onClick={() => addToCart(product, matchedVariant)}
          >
            {needsSelection ? "Select options" : outOfStock ? "Out of stock" : (<>Add to bag <ShoppingBag size={17} /></>)}
          </button>
          <p className="secure-note">Secure checkout with Razorpay · UPI, cards & net banking</p>
        </div>
      </div>

      <section className="reviews-section" data-testid="reviews-section">
        <h2>Maker reviews</h2>
        {reviews.length ? (
          <div className="reviews-list">
            {reviews.map((r) => (
              <article className="review-card" key={r.id} data-testid={`review-card-${r.id}`}>
                <div className="review-head">
                  <strong>{r.author}</strong>
                  <div className="stars">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={13} fill={i < r.rating ? "currentColor" : "none"} />
                    ))}
                  </div>
                </div>
                <p>{r.text}</p>
                <small>{new Date(r.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</small>
              </article>
            ))}
          </div>
        ) : (
          <p className="reviews-empty" data-testid="reviews-empty">Be the first to review this after your order arrives.</p>
        )}
      </section>

      {zoom && (
        <div className="lightbox" data-testid="lightbox" onClick={() => setZoom(false)}>
          <button className="lightbox-close" aria-label="Close" data-testid="lightbox-close"><X size={22} /></button>
          <img src={imageUrl(gallery[active])} alt={product.name} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </main>
  );
}

function Cart({ cart, updateQuantity, total, go, threshold, flatShipping, appliedCoupon, setAppliedCoupon, applyCoupon }) {
  const [codeInput, setCodeInput] = useState(appliedCoupon?.code || "");
  const shipping = total >= threshold ? 0 : (flatShipping ?? 80);
  const discount = appliedCoupon ? Math.min(appliedCoupon.discount, total) : 0;
  const finalTotal = Math.max(0, total + shipping - discount);
  return (
    <main className="simple-page" data-testid="cart-page">
      <div className="page-title">
        <p className="eyebrow">YOUR SELECTION</p>
        <h1>Your bag</h1>
        <span>{cart.length} products</span>
      </div>
      {cart.length ? (
        <div className="cart-layout">
          <div className="cart-list">
            {cart.map((item) => (
              <div className="cart-item" key={item.key || item.id} data-testid={`cart-item-${item.key || item.id}`}>
                <img src={imageUrl(item.image)} alt={item.name} />
                <div className="cart-item-copy">
                  <strong>{item.name}</strong>
                  <span>{item.variant_label || item.unit}</span>
                  <div className="quantity">
                    <button data-testid={`decrease-${item.key || item.id}-button`} onClick={() => updateQuantity(item.key || item.id, -1)}>
                      <Minus size={14} />
                    </button>
                    <b data-testid={`quantity-${item.key || item.id}`}>{item.quantity}</b>
                    <button data-testid={`increase-${item.key || item.id}-button`} onClick={() => updateQuantity(item.key || item.id, 1)}>
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <strong>{money(item.price * item.quantity)}</strong>
              </div>
            ))}
          </div>
          <aside className="summary">
            <p>Order summary</p>
            <div className="coupon-box">
              {appliedCoupon ? (
                <div className="coupon-applied" data-testid="coupon-applied">
                  <Ticket size={14} />
                  <strong>{appliedCoupon.code}</strong>
                  <span>−{money(discount)}</span>
                  <button
                    data-testid="coupon-remove-button"
                    onClick={() => { setAppliedCoupon(null); setCodeInput(""); }}
                    aria-label="Remove coupon"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <form
                  className="coupon-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (codeInput.trim()) applyCoupon(codeInput.trim().toUpperCase());
                  }}
                >
                  <input
                    data-testid="coupon-code-input"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                  />
                  <button data-testid="apply-coupon-button" type="submit">Apply</button>
                </form>
              )}
            </div>
            <div><span>Subtotal</span><b>{money(total)}</b></div>
            <div><span>Shipping</span><b>{shipping === 0 ? "Free" : money(shipping)}</b></div>
            {discount > 0 && (
              <div className="discount-line"><span>Discount</span><b>−{money(discount)}</b></div>
            )}
            <hr />
            <div className="total-row"><span>Total</span><b>{money(finalTotal)}</b></div>
            <button className="pink-button wide" data-testid="proceed-checkout-button" onClick={() => go("checkout")}>
              Continue to checkout <ChevronRight size={17} />
            </button>
          </aside>
        </div>
      ) : (
        <div className="empty-state">
          <ShoppingBag size={35} />
          <h2>Your bag is waiting</h2>
          <p>Pick something lovely for your next pour.</p>
          <button className="pink-button" data-testid="empty-shop-button" onClick={() => go("shop")}>
            Browse materials
          </button>
        </div>
      )}
    </main>
  );
}

function Checkout({ cart, total, go, setCustomer, notify, threshold, flatShipping, appliedCoupon }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const shipping = total >= threshold ? 0 : (flatShipping ?? 80);
  const discount = appliedCoupon ? Math.min(appliedCoupon.discount, total) : 0;
  const finalTotal = Math.max(0, total + shipping - discount);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const saveAbandoned = async () => {
    if (!form.email || !cart.length) return;
    try {
      await axios.post(`${API}/abandoned-cart`, {
        email: form.email,
        name: form.name,
        items: cart.map(({ id, name, price, quantity, image, variant_id, variant_label, color, size }) => ({ product_id: id, name, price, quantity, image, variant_id, variant_label, color, size })),
        total: finalTotal,
      });
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payment = await axios.post(`${API}/payments/create`, { amount: finalTotal });
      const order = await axios.post(`${API}/orders`, {
        customer_name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        items: cart.map(({ id, name, price, quantity, image, variant_id, variant_label, color, size }) => ({ product_id: id, name, price, quantity, image, variant_id, variant_label, color, size })),
        total: finalTotal,
        discount,
        coupon_code: appliedCoupon?.code || null,
        razorpay_order_id: payment.data.id,
      });
      setCustomer(form);
      if (!window.Razorpay) {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        document.body.appendChild(script);
        await new Promise((resolve) => { script.onload = resolve; });
      }
      const razor = new window.Razorpay({
        key: payment.data.key_id,
        amount: payment.data.amount,
        currency: "INR",
        name: "Resin Supplier Shreya",
        description: "Craft supplies order",
        order_id: payment.data.id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#e0568c" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              ...response,
              razorpay_order_id: payment.data.id,
              order_id: order.data.id,
            });
            notify(`Payment received · ${order.data.id}`);
            go("orders");
          } catch (err) {
            // Verification failed — leave the pending order and surface the error.
            notify("Payment could not be verified. Please contact us if you were charged.");
          }
        },
        modal: {
          ondismiss: async () => {
            // User closed Razorpay without paying — delete the pending order so
            // the admin never sees it and no confirmation email goes out.
            try {
              await axios.post(`${API}/orders/${order.data.id}/cancel-pending`);
            } catch {}
            notify("Payment cancelled — your order has not been placed.");
            setBusy(false);
          },
        },
      });
      razor.open();
    } catch (error) {
      notify(error.response?.data?.detail || "Payment could not start. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="simple-page checkout-page" data-testid="checkout-page">
      <button className="back-link" data-testid="checkout-back-button" onClick={() => go("cart")}>
        <ArrowLeft size={16} /> Back to bag
      </button>
      <div className="page-title">
        <p className="eyebrow">ALMOST THERE</p>
        <h1>Checkout</h1>
        <span>Secure payment powered by Razorpay</span>
      </div>
      <form className="checkout-layout" onSubmit={submit}>
        <div className="checkout-form">
          <label>Full name<input data-testid="checkout-name-input" required name="name" value={form.name} onChange={change} placeholder="Your name" /></label>
          <label>Email address<input data-testid="checkout-email-input" required type="email" name="email" value={form.email} onChange={change} onBlur={saveAbandoned} placeholder="you@example.com" /></label>
          <label>Mobile number<input data-testid="checkout-phone-input" required pattern="[0-9]{10}" name="phone" value={form.phone} onChange={change} placeholder="10-digit mobile number" /></label>
          <label>Delivery address<textarea data-testid="checkout-address-input" required name="address" value={form.address} onChange={change} placeholder="House, street, city, PIN code" /></label>
          <button className="pink-button wide" data-testid="pay-now-button" disabled={busy}>
            {busy ? "Opening secure checkout…" : `Pay ${money(finalTotal)}`} <ChevronRight size={17} />
          </button>
        </div>
        <aside className="summary">
          <p>Your order</p>
          {cart.map((item) => (
            <div key={item.id}>
              <span>{item.name} × {item.quantity}</span>
              <b>{money(item.price * item.quantity)}</b>
            </div>
          ))}
          <hr />
          <div><span>Shipping</span><b>{shipping === 0 ? "Free" : money(shipping)}</b></div>
          {appliedCoupon && (
            <div className="discount-line"><span>Coupon {appliedCoupon.code}</span><b>−{money(discount)}</b></div>
          )}
          <div className="total-row"><span>Total</span><b>{money(finalTotal)}</b></div>
          <small>UPI · Cards · Net banking supported</small>
        </aside>
      </form>
    </main>
  );
}

function Orders({ customer, orders, loadOrders, go, onReview }) {
  const [email, setEmail] = useState(customer?.email || "");
  return (
    <main className="simple-page" data-testid="orders-page">
      <div className="page-title">
        <p className="eyebrow">YOUR MAKER JOURNEY</p>
        <h1>Order history</h1>
        <span>Track every package from our studio</span>
      </div>
      <div className="order-lookup">
        <input data-testid="order-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your checkout email" />
        <button className="pink-button" data-testid="load-orders-button" onClick={() => loadOrders(email)}>
          Find my orders
        </button>
      </div>
      {orders.length ? (
        <div className="orders-list">
          {orders.map((order) => (
            <article className="order-card" data-testid={`order-card-${order.id}`} key={order.id}>
              <div>
                <span className="order-id">{order.id}</span>
                <strong>{money(order.total)}</strong>
              </div>
              <p>{order.items.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}</p>
              <div className="status-track">
                {ORDER_STATUSES.map((s, idx) => {
                  const currentIdx = ORDER_STATUSES.indexOf(order.status);
                  return (
                    <span key={s} className={currentIdx >= idx ? "done" : ""}>
                      {idx === 0 && <CheckCircle2 size={16} />}
                      {idx === 1 && <Package size={16} />}
                      {idx === 2 && <Truck size={16} />}
                      {idx === 3 && <Heart size={16} />}
                      {s}
                    </span>
                  );
                })}
              </div>
              <div className="order-card-footer">
                <small>{order.status} · We’ll keep you updated on your delivery</small>
                {order.status === "Delivered" && (
                  <button
                    className="review-cta"
                    data-testid={`review-order-${order.id}-button`}
                    onClick={() => onReview(order)}
                  >
                    <Star size={13} fill="currentColor" /> Rate items
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state" data-testid="orders-empty-state">
          <Package size={35} />
          <h2>No orders here yet</h2>
          <p>Enter the email used at checkout, or start shopping.</p>
          <button className="pink-button" data-testid="orders-shop-button" onClick={() => go("shop")}>
            Shop the collection
          </button>
        </div>
      )}
    </main>
  );
}

function ReviewModal({ order, onClose, notify }) {
  const [items, setItems] = useState(
    order.items.map((i) => ({ product_id: i.product_id, name: i.name, image: i.image, rating: 5, text: "", submitted: false }))
  );
  const [busy, setBusy] = useState(false);

  const submit = async (idx) => {
    const item = items[idx];
    if (item.submitted) return;
    setBusy(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: item.product_id,
        order_id: order.id,
        email: order.email,
        author: order.customer_name,
        rating: item.rating,
        text: item.text,
      });
      setItems((current) => current.map((it, i) => (i === idx ? { ...it, submitted: true } : it)));
      notify("Thanks for your review!");
    } catch (err) {
      notify(err.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="review-modal" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <p className="eyebrow">RATE YOUR ORDER</p>
            <h2>How did we do?</h2>
          </div>
          <button data-testid="review-close-button" onClick={onClose}><X size={17} /></button>
        </div>
        <div className="modal-body">
          {items.map((item, idx) => (
            <div className="review-item" key={item.product_id + idx} data-testid={`review-item-${item.product_id}`}>
              <img src={imageUrl(item.image)} alt="" />
              <div className="review-item-body">
                <strong>{item.name}</strong>
                <div className="rating-picker">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${item.product_id}-${n}`}
                      onClick={() =>
                        setItems((current) => current.map((it, i) => (i === idx ? { ...it, rating: n } : it)))
                      }
                      disabled={item.submitted}
                    >
                      <Star size={20} fill={n <= item.rating ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid={`review-text-${item.product_id}`}
                  placeholder="Share what you loved (optional)"
                  value={item.text}
                  onChange={(e) =>
                    setItems((current) => current.map((it, i) => (i === idx ? { ...it, text: e.target.value } : it)))
                  }
                  disabled={item.submitted}
                />
                <button
                  className="pink-button"
                  data-testid={`review-submit-${item.product_id}`}
                  onClick={() => submit(idx)}
                  disabled={item.submitted || busy}
                >
                  {item.submitted ? "Thanks!" : "Submit review"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SupplierLogin({ setSupplierToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/supplier-login`, { email, password });
      setSupplierToken(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not sign in");
    }
  };
  return (
    <main className="simple-page login-page" data-testid="supplier-login-page">
      <div className="login-box">
        <img src={LOGO} alt="Resin Supplier Shreya" className="login-logo" />
        <p className="eyebrow">PRIVATE STUDIO DESK</p>
        <h1>Welcome, Shreya.</h1>
        <p>Sign in to manage products, banners and orders.</p>
        <form onSubmit={submit}>
          <label>Email<input data-testid="supplier-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Supplier email" /></label>
          <label>Password<input data-testid="supplier-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" /></label>
          {error && <div className="form-error" data-testid="supplier-login-error">{error}</div>}
          <button className="pink-button wide" data-testid="supplier-login-button">Sign in <ChevronRight size={17} /></button>
        </form>
      </div>
    </main>
  );
}

function ImageUploader({ token, current, onUploaded, compact = false }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/uploads`, data, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      onUploaded(res.data.path);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={compact ? "image-uploader compact" : "image-uploader"} data-testid="image-uploader">
      {!compact && (
        <div className="uploader-preview">
          <img src={imageUrl(current)} alt="Preview" />
        </div>
      )}
      <div className="uploader-actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handle}
          data-testid="product-image-file-input"
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="pink-button"
          data-testid="product-image-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          <Upload size={14} /> {uploading ? "Uploading…" : compact ? "Add photo" : "Upload photo"}
        </button>
        {!compact && <small>JPEG/PNG up to 5 MB</small>}
      </div>
    </div>
  );
}

function Dashboard({ categories, loadProducts, loadCategories, loadBanners, loadSettings, notify, token, settings, onLogout }) {
  const headers = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [tab, setTab] = useState("inventory");
  const [editing, setEditing] = useState(null);
  const [editingImages, setEditingImages] = useState([]);
  const [editingColors, setEditingColors] = useState([]);
  const [editingSizes, setEditingSizes] = useState([]);
  const [editingVariants, setEditingVariants] = useState([]);
  const [editingVariantImages, setEditingVariantImages] = useState({});
  const [colorDraft, setColorDraft] = useState("");
  const [sizeDraft, setSizeDraft] = useState("");
  const commitColorDraft = () => {
    const raw = (colorDraft || "").trim();
    if (!raw) return;
    const parts = raw.split(",").map((x) => x.trim()).filter(Boolean);
    setEditingColors((cur) => {
      const next = [...cur];
      parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
      return next;
    });
    setColorDraft("");
  };
  const commitSizeDraft = () => {
    const raw = (sizeDraft || "").trim();
    if (!raw) return;
    const parts = raw.split(",").map((x) => x.trim()).filter(Boolean);
    setEditingSizes((cur) => {
      const next = [...cur];
      parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
      return next;
    });
    setSizeDraft("");
  };
  const [urlInput, setUrlInput] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [allBanners, setAllBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingBannerImage, setEditingBannerImage] = useState("");
  const [editingBannerImageMobile, setEditingBannerImageMobile] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [shippingText, setShippingText] = useState(settings.shipping_bar_text || "");
  const [threshold, setThreshold] = useState(settings.free_shipping_threshold || 1999);
  const [featured, setFeatured] = useState(settings.featured_banner || { active: false, title: "", subtitle: "", image: "", cta_label: "", cta_link: "" });
  const [menuCategories, setMenuCategories] = useState(settings.menu_categories || []);
  const [catalogChipCategories, setCatalogChipCategories] = useState(settings.catalog_categories || []);
  const [catalogEyebrow, setCatalogEyebrow] = useState(settings.catalog_eyebrow || "SHOP THE STUDIO");
  const [catalogHeading, setCatalogHeading] = useState(settings.catalog_heading || "Find your next favourite.");
  const [coupons, setCoupons] = useState([]);
  const [editingCoupon, setEditingCoupon] = useState(null);

  useEffect(() => setShippingText(settings.shipping_bar_text || ""), [settings.shipping_bar_text]);
  useEffect(() => setThreshold(settings.free_shipping_threshold || 1999), [settings.free_shipping_threshold]);
  useEffect(() => {
    setFeatured(settings.featured_banner || { active: false, title: "", subtitle: "", image: "", cta_label: "", cta_link: "" });
  }, [settings.featured_banner]);
  useEffect(() => setMenuCategories(settings.menu_categories || []), [settings.menu_categories]);
  useEffect(() => setCatalogChipCategories(settings.catalog_categories || []), [settings.catalog_categories]);
  useEffect(() => setCatalogEyebrow(settings.catalog_eyebrow || "SHOP THE STUDIO"), [settings.catalog_eyebrow]);
  useEffect(() => setCatalogHeading(settings.catalog_heading || "Find your next favourite."), [settings.catalog_heading]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products/all`, headers);
      setAllProducts(res.data);
    } catch {
      notify("Could not load full inventory");
    }
  }, [headers, notify]);

  const [showUnpaid, setShowUnpaid] = useState(false);
  const [checkingPayments, setCheckingPayments] = useState(false);
  const fetchOrders = useCallback(async () => {
    const res = await axios.get(`${API}/orders`, { ...headers, params: showUnpaid ? { include_pending: true } : {} });
    setSellerOrders(res.data);
  }, [showUnpaid, headers]);

  const fetchAllBanners = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/banners/all`, headers);
      setAllBanners(res.data);
    } catch {
      notify("Could not load banners");
    }
  }, [headers, notify]);

  const fetchCoupons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coupons`, headers);
      setCoupons(res.data);
    } catch {
      notify("Could not load coupons");
    }
  }, [headers, notify]);

  const [insights, setInsights] = useState({ daily: [], totals: {}, top_products: [], window_days: 30 });
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const fetchInsights = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/insights/overview?days=30`, headers);
      setInsights(res.data || { daily: [], totals: {}, top_products: [] });
    } catch { /* ignore */ }
  }, [headers]);
  const fetchAbandonedCarts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/abandoned-carts`, headers);
      setAbandonedCarts(res.data || []);
    } catch { /* ignore */ }
  }, [headers]);
  const resendAbandonedCart = async (id) => {
    try {
      await axios.post(`${API}/abandoned-carts/${id}/resend`, {}, headers);
      notify("Reminder email sent");
      await fetchAbandonedCarts();
    } catch (err) {
      notify(err.response?.data?.detail || "Could not resend reminder");
    }
  };
  const deleteAbandonedCart = async (id) => {
    if (!window.confirm("Remove this abandoned cart from the list?")) return;
    try {
      await axios.delete(`${API}/abandoned-carts/${id}`, headers);
      await fetchAbandonedCarts();
    } catch (err) {
      notify(err.response?.data?.detail || "Could not remove");
    }
  };

  useEffect(() => {
    fetchAllProducts();
    fetchOrders();
    fetchAllBanners();
    fetchCoupons();
    fetchInsights();
    fetchAbandonedCarts();
  }, [fetchAllProducts, fetchOrders, fetchAllBanners, fetchCoupons, fetchInsights, fetchAbandonedCarts]);

  const startEditProduct = (product) => {
    setEditing(product || {});
    const imgs = (product?.images && product.images.length)
      ? product.images
      : (product?.image ? [product.image] : []);
    setEditingImages(imgs);
    setEditingColors(product?.colors || []);
    setEditingSizes(product?.sizes || []);
    setEditingVariants(product?.variants || []);
    setEditingVariantImages(product?.variant_images || {});
    setColorDraft("");
    setSizeDraft("");
    setUrlInput("");
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    // Auto-commit any unfinished text in the color/size drafts
    if (colorDraft.trim()) commitColorDraft();
    if (sizeDraft.trim()) commitSizeDraft();
    if (!editingImages.length) {
      notify("Please add at least one product photo");
      return;
    }
    const data = Object.fromEntries(new FormData(e.currentTarget));
    data.price = Number(data.price);
    data.images = editingImages;
    data.image = editingImages[0];
    // Merge any pending drafts into final colors/sizes
    const pendingColors = colorDraft.trim().split(",").map((x) => x.trim()).filter(Boolean);
    const pendingSizes = sizeDraft.trim().split(",").map((x) => x.trim()).filter(Boolean);
    const finalColors = [...editingColors];
    pendingColors.forEach((p) => { if (!finalColors.includes(p)) finalColors.push(p); });
    const finalSizes = [...editingSizes];
    pendingSizes.forEach((p) => { if (!finalSizes.includes(p)) finalSizes.push(p); });
    data.colors = finalColors;
    data.sizes = finalSizes;
    // Only keep variant_images for colors that still exist
    const cleanedVariantImages = {};
    Object.entries(editingVariantImages || {}).forEach(([k, v]) => {
      if (finalColors.includes(k) && Array.isArray(v) && v.length) cleanedVariantImages[k] = v;
    });
    data.variant_images = cleanedVariantImages;
    data.variants = editingVariants.map((v) => ({
      ...v,
      price: v.price === "" || v.price === null || v.price === undefined ? null : Number(v.price),
      stock: v.stock === "" || v.stock === null || v.stock === undefined ? 999 : Number(v.stock),
    }));
    try {
      if (editing?.id) await axios.patch(`${API}/products/${editing.id}`, data, headers);
      else await axios.post(`${API}/products`, data, headers);
      setEditing(null);
      await Promise.all([loadProducts(), fetchAllProducts()]);
      notify(editing?.id ? "Product updated" : "Product added");
    } catch (err) {
      notify(err.response?.data?.detail || "Could not save product");
    }
  };

  const archiveProduct = async (id) => {
    if (!window.confirm("Archive this product? It stays in past orders but is hidden from the storefront.")) return;
    try {
      await axios.post(`${API}/products/${id}/archive`, {}, headers);
      await Promise.all([loadProducts(), fetchAllProducts()]);
      notify("Product archived");
    } catch { notify("Could not archive"); }
  };

  const restoreProduct = async (id) => {
    try {
      await axios.post(`${API}/products/${id}/restore`, {}, headers);
      await Promise.all([loadProducts(), fetchAllProducts()]);
      notify("Product restored");
    } catch { notify("Could not restore"); }
  };

  const shareProduct = async (id) => {
    const url = `${window.location.origin}/?product=${id}`;
    try {
      await navigator.clipboard.writeText(url);
      notify("Product link copied to clipboard");
    } catch { notify(url); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}/status`, { status }, headers);
      setSellerOrders((current) => current.map((o) => (o.id === id ? { ...o, status } : o)));
      notify("Order status updated");
    } catch { notify("Could not update status"); }
  };

  const resendOrderEmails = async (id) => {
    try {
      const res = await axios.post(`${API}/orders/${id}/resend-emails`, {}, headers);
      notify(res.data.scheduled?.length ? "Emails are being resent" : "No email address to send to");
      setTimeout(() => fetchOrders().catch(() => {}), 4000);
    } catch (err) {
      notify(err.response?.data?.detail || "Could not resend emails");
    }
  };

  const checkPendingPayments = async () => {
    setCheckingPayments(true);
    try {
      const res = await axios.post(`${API}/orders/reconcile-payments`, {}, headers);
      const n = res.data.rescued?.length || 0;
      notify(n ? `Recovered ${n} paid order${n > 1 ? "s" : ""}` : "No missed payments found");
      await fetchOrders();
    } catch (err) {
      notify(err.response?.data?.detail || "Could not check payments");
    } finally {
      setCheckingPayments(false);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newCategory.trim() }, headers);
      setNewCategory("");
      await loadCategories();
      notify("Category added");
    } catch (err) { notify(err.response?.data?.detail || "Could not add category"); }
  };

  const removeCategory = async (name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try {
      await axios.delete(`${API}/categories/${encodeURIComponent(name)}`, headers);
      await loadCategories();
      notify("Category removed");
    } catch (err) { notify(err.response?.data?.detail || "Could not remove category"); }
  };

  const renameCategory = async (name) => {
    const next = window.prompt(`Rename "${name}" to:`, name);
    if (!next || !next.trim() || next.trim() === name) return;
    try {
      await axios.patch(`${API}/categories/${encodeURIComponent(name)}`, { new_name: next.trim() }, headers);
      await Promise.all([loadCategories(), loadProducts(), fetchAllProducts()]);
      notify("Category renamed");
    } catch (err) { notify(err.response?.data?.detail || "Could not rename category"); }
  };

  const startEditBanner = (banner) => {
    setEditingBanner(banner || {});
    setEditingBannerImage(banner?.image || DEFAULT_IMAGE);
    setEditingBannerImageMobile(banner?.image_mobile || "");
  };

  const saveBanner = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    data.order = Number(data.order || 0);
    data.active = data.active === "on";
    data.link = data.link || null;
    data.image = editingBannerImage || DEFAULT_IMAGE;
    data.image_mobile = editingBannerImageMobile || null;
    try {
      if (editingBanner?.id) await axios.patch(`${API}/banners/${editingBanner.id}`, data, headers);
      else await axios.post(`${API}/banners`, data, headers);
      setEditingBanner(null);
      await Promise.all([fetchAllBanners(), loadBanners()]);
      notify("Banner saved");
    } catch { notify("Could not save banner"); }
  };

  const removeBanner = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await axios.delete(`${API}/banners/${id}`, headers);
      await Promise.all([fetchAllBanners(), loadBanners()]);
      notify("Banner deleted");
    } catch { notify("Could not delete banner"); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/settings`,
        {
          shipping_bar_text: shippingText,
          free_shipping_threshold: Number(threshold),
          featured_banner: featured,
          menu_categories: menuCategories,
          catalog_categories: catalogChipCategories,
          catalog_eyebrow: catalogEyebrow,
          catalog_heading: catalogHeading,
        },
        headers
      );
      await loadSettings();
      notify("Storefront settings saved");
    } catch { notify("Could not save settings"); }
  };

  const toggleMenuCategory = (name) => {
    setMenuCategories((cur) => (cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]));
  };
  const toggleCatalogChip = (name) => {
    setCatalogChipCategories((cur) => (cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]));
  };

  const saveCoupon = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    data.value = Number(data.value);
    data.min_cart = Number(data.min_cart || 0);
    data.active = data.active === "on";
    data.expires_at = data.expires_at || null;
    try {
      if (editingCoupon?.id) await axios.patch(`${API}/coupons/${editingCoupon.id}`, data, headers);
      else await axios.post(`${API}/coupons`, data, headers);
      setEditingCoupon(null);
      await fetchCoupons();
      notify("Coupon saved");
    } catch (err) { notify(err.response?.data?.detail || "Could not save coupon"); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, headers);
      await fetchCoupons();
      notify("Coupon deleted");
    } catch { notify("Could not delete coupon"); }
  };

  const [adminSearch, setAdminSearch] = useState("");
  const matchesAdminSearch = (p) => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
  };
  const liveList = allProducts.filter((p) => !p.archived && matchesAdminSearch(p));
  const archivedList = allProducts.filter((p) => p.archived && matchesAdminSearch(p));

  return (
    <main className="dashboard" data-testid="supplier-dashboard">
      <div className="dashboard-top">
        <div>
          <p className="eyebrow">SHREYA’S STUDIO</p>
          <h1>Good morning, Shreya.</h1>
          <span>Manage your materials, banners, coupons and orders in one place.</span>
        </div>
        <div className="dashboard-actions">
          {tab === "inventory" && (
            <button className="pink-button" data-testid="add-product-button" onClick={() => startEditProduct(null)}>
              <Plus size={17} /> Add material
            </button>
          )}
          <button className="logout-button" data-testid="supplier-logout-button" onClick={onLogout}>
            <LogOut size={15} /> Log out
          </button>
        </div>
      </div>
      <div className="stats-row">
        <div><span>Live materials</span><strong>{liveList.length}</strong><small>in catalogue</small></div>
        <div><span>Orders</span><strong>{sellerOrders.filter((o) => !UNPAID.includes(o.status)).length}</strong><small>all time</small></div>
        <div><span>Banners</span><strong>{allBanners.length}</strong><small>configured</small></div>
        <div><span>Coupons</span><strong>{coupons.filter((c) => c.active).length}</strong><small>active</small></div>
      </div>
      <div className="dashboard-tabs">
        <button data-testid="inventory-tab-button" className={tab === "inventory" ? "active" : ""} onClick={() => setTab("inventory")}><Package size={17} /> Products</button>
        <button data-testid="supplier-orders-tab-button" className={tab === "orders" ? "active" : ""} onClick={() => setTab("orders")}><Truck size={17} /> Orders</button>
        <button data-testid="banners-tab-button" className={tab === "banners" ? "active" : ""} onClick={() => setTab("banners")}><ImagePlus size={17} /> Banners</button>
        <button data-testid="coupons-tab-button" className={tab === "coupons" ? "active" : ""} onClick={() => setTab("coupons")}><Ticket size={17} /> Coupons</button>
        <button data-testid="storefront-tab-button" className={tab === "storefront" ? "active" : ""} onClick={() => setTab("storefront")}><Settings2 size={17} /> Home page</button>
        <button data-testid="insights-tab-button" className={tab === "insights" ? "active" : ""} onClick={() => setTab("insights")}><BarChart3 size={17} /> Insights</button>
      </div>

      {editing && tab === "inventory" && (
        <form className="product-editor" data-testid="product-editor-form" onSubmit={saveProduct}>
          <div className="editor-heading">
            <h2>{editing.id ? "Edit material" : "New material"}</h2>
            <button type="button" data-testid="cancel-product-button" onClick={() => setEditing(null)}><X size={17} /></button>
          </div>
          <label>Name<input required name="name" defaultValue={editing.name || ""} data-testid="product-name-input" /></label>
          <label>Category
            <select name="category" defaultValue={editing.category || categories[1] || "Epoxy Resin"} data-testid="product-category-select">
              {categories.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label>Price (₹)<input required type="number" step="0.01" name="price" defaultValue={editing.price || ""} data-testid="product-price-input" /></label>
          <label>Unit label<input name="unit" defaultValue={editing.unit || "500 g"} data-testid="product-unit-input" /></label>
          <label className="span-2">Description<textarea required name="description" defaultValue={editing.description || ""} data-testid="product-description-input" /></label>
          <div className="span-2">
            <p className="tiny-label">Product photos (first is the cover)</p>
            <div className="gallery-editor" data-testid="product-gallery-editor">
              {editingImages.length === 0 && (
                <div className="gallery-empty" data-testid="gallery-empty-message">
                  <ImagePlus size={22} />
                  <span>No photo yet — upload one or add an image URL below</span>
                </div>
              )}
              {editingImages.map((img, idx) => (
                <div className="gallery-editor-tile" key={img + idx} data-testid={`product-image-tile-${idx}`}>
                  <img src={imageUrl(img)} alt="" />
                  {idx === 0 && <span className="cover-tag">Cover</span>}
                  <button
                    type="button"
                    data-testid={`product-image-remove-${idx}`}
                    onClick={() => setEditingImages((cur) => cur.filter((_, i) => i !== idx))}
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <div className="gallery-editor-add">
                <ImageUploader
                  token={token}
                  current={null}
                  onUploaded={(p) => setEditingImages((cur) => [...cur, p])}
                  compact
                />
              </div>
            </div>
            <details className="url-fallback">
              <summary>or add by image URL</summary>
              <div className="url-add-row">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://…"
                  data-testid="product-image-url-input"
                />
                <button
                  type="button"
                  data-testid="product-image-url-add"
                  onClick={() => {
                    const val = urlInput.trim();
                    if (val) {
                      setEditingImages((cur) => [...cur, val]);
                      setUrlInput("");
                    }
                  }}
                >
                  <Plus size={14} /> Add
                </button>
              </div>
            </details>
          </div>
          <div className="span-2 variant-editor" data-testid="variant-editor">
            <p className="tiny-label">Options (colors &amp; sizes for swatch selector)</p>
            <p className="variant-help">Tip: type a value and press <b>Enter</b> or <b>,</b> — or paste multiple like <i>Red, Blue, Green</i> then click Add.</p>
            <div className="variant-chips-row">
              <span className="chip-heading">Colors</span>
              {editingColors.map((c) => (
                <span className="tag-chip" key={c}>{c}
                  <button type="button" onClick={() => setEditingColors((cur) => cur.filter((x) => x !== c))} data-testid={`remove-color-${c.toLowerCase().replace(/\s+/g,"-")}`}><X size={10} /></button>
                </span>
              ))}
              <input
                type="text"
                placeholder="Add colors (comma-separated OK)"
                data-testid="color-input"
                value={colorDraft}
                onChange={(e) => setColorDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    commitColorDraft();
                  }
                }}
                onBlur={commitColorDraft}
              />
              <button type="button" className="pink-button ghost" onClick={commitColorDraft} data-testid="add-color-button"><Plus size={12} /> Add</button>
            </div>
            <div className="variant-chips-row">
              <span className="chip-heading">Sizes</span>
              {editingSizes.map((s) => (
                <span className="tag-chip" key={s}>{s}
                  <button type="button" onClick={() => setEditingSizes((cur) => cur.filter((x) => x !== s))} data-testid={`remove-size-${s.toLowerCase().replace(/\s+/g,"-")}`}><X size={10} /></button>
                </span>
              ))}
              <input
                type="text"
                placeholder="Add sizes (comma-separated OK)"
                data-testid="size-input"
                value={sizeDraft}
                onChange={(e) => setSizeDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    commitSizeDraft();
                  }
                }}
                onBlur={commitSizeDraft}
              />
              <button type="button" className="pink-button ghost" onClick={commitSizeDraft} data-testid="add-size-button"><Plus size={12} /> Add</button>
            </div>
            {editingColors.length > 0 && (
              <div className="color-galleries" data-testid="color-galleries">
                <p className="tiny-label">Per-color photos (optional — falls back to main product photos)</p>
                {editingColors.map((c) => {
                  const imgs = editingVariantImages[c] || [];
                  return (
                    <div className="color-gallery" key={c}>
                      <div className="color-gallery-head">
                        <span className="swatch" style={{ backgroundColor: c.toLowerCase() || "#f4d3dc" }} />
                        <b>{c}</b>
                        <small>{imgs.length} photo{imgs.length === 1 ? "" : "s"}</small>
                      </div>
                      <div className="color-gallery-thumbs">
                        {imgs.map((img, idx) => (
                          <div className="thumb-tile" key={img + idx}>
                            <img src={imageUrl(img)} alt="" />
                            <button
                              type="button"
                              className="remove-thumb"
                              onClick={() => setEditingVariantImages((cur) => ({ ...cur, [c]: (cur[c] || []).filter((_, i) => i !== idx) }))}
                              data-testid={`remove-color-image-${c.toLowerCase().replace(/\s+/g, "-")}-${idx}`}
                            ><X size={10} /></button>
                          </div>
                        ))}
                        <ImageUploader
                          token={token}
                          current=""
                          compact
                          onUploaded={(p) => setEditingVariantImages((cur) => ({ ...cur, [c]: [...(cur[c] || []), p] }))}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="variant-matrix-wrap" data-testid="variant-matrix-wrap">
              <div className="variant-matrix-head">
                <p className="tiny-label" style={{ margin: 0 }}>Variant combinations &amp; pricing</p>
                <div className="matrix-actions">
                  {(editingColors.length > 0 || editingSizes.length > 0) && (
                    <button
                      type="button"
                      className="pink-button ghost"
                      data-testid="auto-generate-variants"
                      onClick={() => {
                        const colors = editingColors.length ? editingColors : [""];
                        const sizes = editingSizes.length ? editingSizes : [""];
                        const combos = [];
                        colors.forEach((c) => {
                          sizes.forEach((s) => {
                            if (!c && !s) return;
                            const match = editingVariants.find((v) => (v.color || "") === (c || "") && (v.size || "") === (s || ""));
                            combos.push(match || { color: c || "", size: s || "", price: "", stock: 999 });
                          });
                        });
                        setEditingVariants(combos);
                      }}
                    ><Sparkles size={12} /> Auto-generate from colors × sizes</button>
                  )}
                  <button
                    type="button"
                    className="pink-button ghost"
                    data-testid="add-variant-row"
                    onClick={() => setEditingVariants((cur) => [...cur, { color: editingColors[0] || "", size: editingSizes[0] || "", price: "", stock: 999 }])}
                  ><Plus size={12} /> Add row</button>
                </div>
              </div>
              <p className="variant-help" style={{ marginTop: 6 }}>Set a specific price for each combo, or leave blank to use the base price. Blank stock defaults to 999.</p>
              <div className="variant-matrix" data-testid="variant-matrix">
                {editingVariants.length > 0 && (
                  <div className="variant-row variant-row-head">
                    <span>Color</span><span>Size</span><span>Price (₹)</span><span>Stock</span><span></span>
                  </div>
                )}
                {editingVariants.map((v, idx) => (
                  <div className="variant-row" key={idx}>
                    <input
                      value={v.color || ""}
                      placeholder="Color"
                      onChange={(e) => setEditingVariants((cur) => cur.map((r, i) => i === idx ? { ...r, color: e.target.value } : r))}
                      data-testid={`variant-color-${idx}`}
                    />
                    <input
                      value={v.size || ""}
                      placeholder="Size"
                      onChange={(e) => setEditingVariants((cur) => cur.map((r, i) => i === idx ? { ...r, size: e.target.value } : r))}
                      data-testid={`variant-size-${idx}`}
                    />
                    <input
                      type="number" step="0.01"
                      value={v.price ?? ""}
                      placeholder="Price"
                      onChange={(e) => setEditingVariants((cur) => cur.map((r, i) => i === idx ? { ...r, price: e.target.value } : r))}
                      data-testid={`variant-price-${idx}`}
                    />
                    <input
                      type="number"
                      value={v.stock ?? ""}
                      placeholder="Stock"
                      onChange={(e) => setEditingVariants((cur) => cur.map((r, i) => i === idx ? { ...r, stock: e.target.value } : r))}
                      data-testid={`variant-stock-${idx}`}
                    />
                    <button type="button" className="ghost-btn" onClick={() => setEditingVariants((cur) => cur.filter((_, i) => i !== idx))} data-testid={`variant-remove-${idx}`}><Trash2 size={12} /></button>
                  </div>
                ))}
                {!editingVariants.length && (
                  <p className="tiny-label" style={{ margin: "8px 0", opacity: .8 }}>No combinations yet — add colors/sizes above then click "Auto-generate".</p>
                )}
              </div>
            </div>
          </div>
          <button className="pink-button" data-testid="save-product-button">Save material</button>
        </form>
      )}

      {tab === "inventory" && (
        <>
          <div className="admin-search-row">
            <Search size={16} />
            <input
              type="search"
              placeholder="Search materials by name or category…"
              value={adminSearch}
              onChange={(e) => setAdminSearch(e.target.value)}
              data-testid="admin-search-input"
            />
            {adminSearch && <button type="button" onClick={() => setAdminSearch("")} className="clear-btn" data-testid="admin-search-clear"><X size={14} /></button>}
          </div>
          <div className="inventory-table" data-testid="inventory-table">
            <div className="table-head">
              <span>Material</span><span>Category</span><span>Price</span><span>Status</span><span>Actions</span>
            </div>
            {liveList.map((p) => (
              <div className="table-row" data-testid={`inventory-row-${p.id}`} key={p.id}>
                <div className="table-product">
                  <img src={imageUrl(p.image)} alt="" />
                  <strong>{p.name}</strong>
                </div>
                <span>{p.category}</span>
                <span>{money(p.price)}</span>
                <span className="stock-ok">Live</span>
                <div className="row-actions">
                  <button data-testid={`edit-product-${p.id}-button`} onClick={() => startEditProduct(p)}>Edit</button>
                  <button data-testid={`share-product-${p.id}-button`} onClick={() => shareProduct(p.id)} title="Copy shareable link"><Share2 size={14} /></button>
                  <button data-testid={`archive-product-${p.id}-button`} onClick={() => archiveProduct(p.id)} title="Archive"><Archive size={14} /></button>
                </div>
              </div>
            ))}
            {!liveList.length && <div className="empty-state small"><Package size={26} /><p>No live materials yet.</p></div>}
          </div>

          {archivedList.length > 0 && (
            <>
              <h3 className="archive-heading">Archived</h3>
              <div className="inventory-table archived" data-testid="archived-table">
                {archivedList.map((p) => (
                  <div className="table-row" key={p.id}>
                    <div className="table-product">
                      <img src={imageUrl(p.image)} alt="" />
                      <strong>{p.name}</strong>
                    </div>
                    <span>{p.category}</span>
                    <span>{money(p.price)}</span>
                    <span className="low-stock">Archived</span>
                    <div className="row-actions">
                      <button data-testid={`restore-product-${p.id}-button`} onClick={() => restoreProduct(p.id)}><Undo2 size={14} /> Restore</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === "orders" && (
        <div className="orders-admin" data-testid="orders-admin">
          <div className="orders-admin-toolbar">
            <button type="button" className="pink-button ghost" data-testid="check-pending-payments-button" onClick={checkPendingPayments} disabled={checkingPayments}>
              {checkingPayments ? "Checking Razorpay…" : "Check missed payments"}
            </button>
            <label className="unpaid-toggle">
              <input type="checkbox" data-testid="show-unpaid-orders-toggle" checked={showUnpaid} onChange={(e) => setShowUnpaid(e.target.checked)} />
              Show unpaid checkout attempts
            </label>
          </div>
          {sellerOrders.length ? sellerOrders.map((o) => (
            <article className="order-admin-card" key={o.id} data-testid={`admin-order-${o.id}`}>
              <header onClick={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)} className="order-admin-head">
                <div>
                  <span className="order-id">{o.id}</span>
                  <strong>{o.customer_name}</strong>
                  <small>{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</small>
                </div>
                <div className="order-admin-meta">
                  <b>{money(o.total)}</b>
                  <span className={`status-pill status-${o.status.replace(/\s+/g, "-").toLowerCase()}`}>{STATUS_LABELS[o.status] || o.status}</span>
                </div>
              </header>
              {expandedOrder === o.id && (
                <div className="order-admin-body">
                  <div className="order-admin-grid">
                    <div>
                      <p className="tiny-label">Contact</p>
                      <p>{o.email}</p>
                      <p>{o.phone}</p>
                    </div>
                    <div>
                      <p className="tiny-label">Delivery address</p>
                      <p>{o.address}</p>
                    </div>
                  </div>
                  {o.coupon_code && (
                    <p className="tiny-label">Coupon: <strong>{o.coupon_code}</strong> (−{money(o.discount || 0)})</p>
                  )}
                  <p className="tiny-label">Items</p>
                  <ul className="order-items-list">
                    {o.items.map((it, i) => (
                      <li key={i}>
                        <img src={imageUrl(it.image)} alt="" />
                        <span>{it.name} × {it.quantity}</span>
                        <b>{money(it.price * it.quantity)}</b>
                      </li>
                    ))}
                  </ul>
                  <div className="order-status-row">
                    <span className="tiny-label">Update status</span>
                    <select
                      data-testid={`order-status-${o.id}-select`}
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                    >
                      {!ORDER_STATUSES.includes(o.status) && <option value={o.status} disabled>{STATUS_LABELS[o.status] || o.status}</option>}
                      {ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <button
                      type="button"
                      className="pink-button ghost"
                      data-testid={`resend-order-emails-${o.id}`}
                      onClick={() => resendOrderEmails(o.id)}
                      title="Resend confirmation to buyer AND notification to owner"
                    ><Mail size={13} /> Resend emails</button>
                  </div>
                  {o.email_log && o.email_log.length > 0 && (
                    <details className="email-log" data-testid={`email-log-${o.id}`}>
                      <summary>Email log ({o.email_log.length})</summary>
                      <ul>
                        {o.email_log.map((e, i) => (
                          <li key={i} className={e.ok ? "email-ok" : "email-fail"}>
                            <b>{e.slot}</b> → {e.to} · {e.ok ? "sent" : "FAILED"}{e.error ? `: ${e.error}` : ""}
                            <small>{e.attempted_at}</small>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </article>
          )) : (
            <div className="empty-state"><Package size={30} /><p>No orders yet.</p></div>
          )}
        </div>
      )}

      {tab === "banners" && (
        <div className="banners-panel" data-testid="banners-panel">
          <div className="panel-head">
            <div><h2>Hero slider slides</h2><span>Each banner becomes a rotating slide on the storefront home page.</span></div>
            <button className="pink-button" data-testid="add-banner-button" onClick={() => startEditBanner(null)}>
              <Plus size={16} /> New slide
            </button>
          </div>
          {editingBanner && (
            <form className="product-editor banner-editor" onSubmit={saveBanner} data-testid="banner-editor-form">
              <div className="editor-heading">
                <h2>{editingBanner.id ? "Edit slide" : "New slide"}</h2>
                <button type="button" onClick={() => setEditingBanner(null)}><X size={17} /></button>
              </div>
              <label>Headline (optional)<input name="title" defaultValue={editingBanner.title || ""} data-testid="banner-title-input" placeholder="Stunning resin creations" /></label>
              <label>Eyebrow / subtitle<input name="subtitle" defaultValue={editingBanner.subtitle || ""} data-testid="banner-subtitle-input" placeholder="LIMITED TIME · 15% OFF" /></label>
              <div className="span-2">
                <p className="tiny-label">Slide image (landscape works best)</p>
                <ImageUploader token={token} current={editingBannerImage} onUploaded={setEditingBannerImage} />
                <details className="url-fallback">
                  <summary>or paste an image URL</summary>
                  <input
                    type="url"
                    value={editingBannerImage.startsWith("http") ? editingBannerImage : ""}
                    onChange={(e) => setEditingBannerImage(e.target.value)}
                    placeholder="https://…"
                    data-testid="banner-image-url-input"
                  />
                </details>
              </div>
              <div className="span-2">
                <p className="tiny-label">Mobile image (portrait / square — optional, falls back to slide image)</p>
                <ImageUploader token={token} current={editingBannerImageMobile} onUploaded={setEditingBannerImageMobile} />
                <details className="url-fallback">
                  <summary>or paste a mobile image URL</summary>
                  <input
                    type="url"
                    value={(editingBannerImageMobile || "").startsWith("http") ? editingBannerImageMobile : ""}
                    onChange={(e) => setEditingBannerImageMobile(e.target.value)}
                    placeholder="https://…"
                    data-testid="banner-image-mobile-input"
                  />
                </details>
              </div>
              <label>Button label<input name="cta_label" defaultValue={editingBanner.cta_label || ""} placeholder="Shop the sale" data-testid="banner-cta-label-input" /></label>
              <label>Button link (optional)<input name="link" defaultValue={editingBanner.link || ""} placeholder="#catalogue or https://…" data-testid="banner-link-input" /></label>
              <label>Order<input type="number" name="order" defaultValue={editingBanner.order || 0} data-testid="banner-order-input" /></label>
              <label className="checkbox-label">
                <input type="checkbox" name="active" defaultChecked={editingBanner.active !== false} data-testid="banner-active-input" /> Active
              </label>
              <button className="pink-button" data-testid="save-banner-button">Save slide</button>
            </form>
          )}
          <div className="banner-grid">
            {allBanners.map((b) => (
              <div className="banner-tile" key={b.id} data-testid={`banner-tile-${b.id}`}>
                <img src={imageUrl(b.image)} alt={b.title} />
                <div className="banner-tile-body">
                  <strong>{b.title}</strong>
                  {b.subtitle && <span>{b.subtitle}</span>}
                  <small>{b.active ? "Active" : "Hidden"} · order {b.order}</small>
                  <div className="row-actions">
                    <button onClick={() => startEditBanner(b)}>Edit</button>
                    <button onClick={() => removeBanner(b.id)} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
            {!allBanners.length && <div className="empty-state small"><ImagePlus size={26} /><p>Add your first slide to bring the home page to life.</p></div>}
          </div>
        </div>
      )}

      {tab === "coupons" && (
        <div className="coupons-panel" data-testid="coupons-panel">
          <div className="panel-head">
            <div><h2>Discount coupons</h2><span>Percent-off or flat-off codes for your buyers.</span></div>
            <button className="pink-button" data-testid="add-coupon-button" onClick={() => setEditingCoupon({})}>
              <Plus size={16} /> New coupon
            </button>
          </div>
          {editingCoupon && (
            <form className="product-editor coupon-editor" onSubmit={saveCoupon} data-testid="coupon-editor-form">
              <div className="editor-heading">
                <h2>{editingCoupon.id ? "Edit coupon" : "New coupon"}</h2>
                <button type="button" onClick={() => setEditingCoupon(null)}><X size={17} /></button>
              </div>
              <label>Code<input required name="code" defaultValue={editingCoupon.code || ""} placeholder="RESIN10" data-testid="coupon-code-input-admin" style={{ textTransform: "uppercase" }} /></label>
              <label>Type
                <select name="type" defaultValue={editingCoupon.type || "percent"} data-testid="coupon-type-select">
                  <option value="percent">Percent off (%)</option>
                  <option value="flat">Flat off (₹)</option>
                </select>
              </label>
              <label>Value<input required type="number" step="0.01" name="value" defaultValue={editingCoupon.value ?? ""} data-testid="coupon-value-input" /></label>
              <label>Min cart (₹)<input type="number" step="0.01" name="min_cart" defaultValue={editingCoupon.min_cart ?? 0} data-testid="coupon-min-cart-input" /></label>
              <label>Expires on<input type="date" name="expires_at" defaultValue={editingCoupon.expires_at ? editingCoupon.expires_at.slice(0, 10) : ""} data-testid="coupon-expires-input" /></label>
              <label className="checkbox-label">
                <input type="checkbox" name="active" defaultChecked={editingCoupon.active !== false} data-testid="coupon-active-input" /> Active
              </label>
              <button className="pink-button" data-testid="save-coupon-button">Save coupon</button>
            </form>
          )}
          <div className="coupons-table">
            <div className="table-head">
              <span>Code</span><span>Discount</span><span>Min cart</span><span>Expires</span><span>Actions</span>
            </div>
            {coupons.map((c) => (
              <div className="table-row" key={c.id} data-testid={`coupon-row-${c.id}`}>
                <div className="table-product"><Ticket size={16} /><strong>{c.code}</strong></div>
                <span>{c.type === "percent" ? `${c.value}%` : money(c.value)}</span>
                <span>{c.min_cart ? money(c.min_cart) : "—"}</span>
                <span>{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-IN") : "No expiry"}</span>
                <div className="row-actions">
                  <button onClick={() => setEditingCoupon(c)}>Edit</button>
                  <button onClick={() => deleteCoupon(c.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {!coupons.length && <div className="empty-state small"><Ticket size={26} /><p>No coupons yet. Create your first promo code above.</p></div>}
          </div>
        </div>
      )}

      {tab === "storefront" && (
        <div className="storefront-panel" data-testid="storefront-panel">
          <section className="setting-card" data-testid="header-menu-card">
            <div className="panel-head">
              <div><h2>Header menu</h2><span>Pick which categories show inside the "Categories" dropdown of the storefront header.</span></div>
            </div>
            <form onSubmit={saveSettings} className="settings-form">
              <div className="menu-checkboxes" data-testid="menu-categories-list">
                {categories.filter((c) => c !== "All").map((c) => {
                  const shown = menuCategories.length === 0 || menuCategories.includes(c);
                  return (
                    <label key={c} className="menu-check-row">
                      <input
                        type="checkbox"
                        checked={shown}
                        onChange={() => {
                          if (menuCategories.length === 0) {
                            // Was "all"; switch to explicit list without this one
                            setMenuCategories(categories.filter((x) => x !== "All" && x !== c));
                          } else {
                            toggleMenuCategory(c);
                          }
                        }}
                        data-testid={`menu-toggle-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                      <span>{c}</span>
                    </label>
                  );
                })}
                {categories.length <= 1 && <p className="tiny-label">Add categories below first.</p>}
              </div>
              <small className="hint">Uncheck to hide a category from the header menu. Storefront filter chips still show everything.</small>
              <button className="pink-button" data-testid="save-menu-button">Save header menu</button>
            </form>
          </section>

          <section className="setting-card" data-testid="catalog-section-card">
            <div className="panel-head">
              <div><h2>"Find your next favourite" section</h2><span>Edit the eyebrow, heading, and which category chips appear on the home page.</span></div>
            </div>
            <form onSubmit={saveSettings} className="settings-form">
              <label>Eyebrow (small caps)<input value={catalogEyebrow} onChange={(e) => setCatalogEyebrow(e.target.value)} data-testid="catalog-eyebrow-input" placeholder="SHOP THE STUDIO" /></label>
              <label>Heading<input value={catalogHeading} onChange={(e) => setCatalogHeading(e.target.value)} data-testid="catalog-heading-input" placeholder="Find your next favourite." /></label>
              <div>
                <p className="tiny-label">Category chips shown on home ({catalogChipCategories.length ? `${catalogChipCategories.length} selected` : "showing all"})</p>
                <div className="menu-checkboxes" data-testid="catalog-chip-list">
                  {categories.filter((c) => c !== "All").map((c) => {
                    const on = catalogChipCategories.length === 0 || catalogChipCategories.includes(c);
                    return (
                      <label key={c} className="menu-check-row">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => {
                            if (catalogChipCategories.length === 0) {
                              setCatalogChipCategories(categories.filter((x) => x !== "All" && x !== c));
                            } else {
                              toggleCatalogChip(c);
                            }
                          }}
                          data-testid={`catalog-chip-toggle-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        />
                        <span>{c}</span>
                      </label>
                    );
                  })}
                </div>
                <small className="hint">Uncheck a category to hide its chip on the home page.</small>
              </div>
              <button className="pink-button" data-testid="save-catalog-button">Save section</button>
            </form>
          </section>

          <section className="setting-card">
            <div className="panel-head">
              <div><h2>Shipping bar</h2><span>Message shown at the very top of the shop.</span></div>
            </div>
            <form onSubmit={saveSettings} className="settings-form">
              <label>Announcement text<input value={shippingText} onChange={(e) => setShippingText(e.target.value)} data-testid="shipping-bar-input" /></label>
              <label>Free shipping threshold (₹)<input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="shipping-threshold-input" /></label>
              <button className="pink-button" data-testid="save-settings-button">Save shipping bar</button>
            </form>
          </section>
          <section className="setting-card">
            <div className="panel-head">
              <div><h2>Categories</h2><span>Add or remove the shop's product categories. Every category is available for filtering; use the Header menu section above to choose which ones appear in the top nav dropdown.</span></div>
            </div>
            <form className="category-form" onSubmit={addCategory} data-testid="add-category-form">
              <Tags size={16} />
              <input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category name" data-testid="new-category-input" />
              <button className="pink-button" data-testid="add-category-button"><Plus size={14} /> Add</button>
            </form>
            <div className="category-list">
              {categories.filter((c) => c !== "All").map((c) => (
                <div className="category-chip" key={c} data-testid={`category-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span>{c}</span>
                  <button onClick={() => renameCategory(c)} title="Rename" data-testid={`rename-category-${c.toLowerCase().replace(/\s+/g, "-")}-button`}><Pencil size={12} /></button>
                  <button onClick={() => removeCategory(c)} title="Remove" data-testid={`delete-category-${c.toLowerCase().replace(/\s+/g, "-")}-button`}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === "insights" && (
        <div className="insights-grid" data-testid="insights-panel">
          <section className="setting-card insights-overview" data-testid="insights-visitors">
            <div className="panel-head">
              <div><h2>Unique visitors</h2><span>Anonymous page-view count by browser · last 30 days</span></div>
            </div>
            <div className="kpi-row">
              <div className="kpi-card" data-testid="kpi-today">
                <small>Today</small>
                <strong>{insights.totals?.today ?? 0}</strong>
              </div>
              <div className="kpi-card" data-testid="kpi-yesterday">
                <small>Yesterday</small>
                <strong>{insights.totals?.yesterday ?? 0}</strong>
              </div>
              <div className="kpi-card" data-testid="kpi-7d">
                <small>Last 7 days</small>
                <strong>{insights.totals?.last_7_days ?? 0}</strong>
              </div>
              <div className="kpi-card" data-testid="kpi-30d">
                <small>Last 30 days</small>
                <strong>{insights.totals?.last_30_days ?? 0}</strong>
              </div>
            </div>
            {(() => {
              const daily = insights.daily || [];
              const max = Math.max(1, ...daily.map((d) => d.unique_visitors || 0));
              return (
                <div className="visit-chart" data-testid="visit-chart">
                  {daily.map((d) => {
                    const h = Math.round(((d.unique_visitors || 0) / max) * 100);
                    const label = d.date.slice(5);
                    return (
                      <div className="visit-bar-wrap" key={d.date} title={`${d.date}: ${d.unique_visitors} unique visitor${d.unique_visitors === 1 ? "" : "s"}`}>
                        <span className="visit-bar" style={{ height: `${h}%` }} />
                        <small>{label}</small>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </section>

          <section className="setting-card" data-testid="insights-top-products">
            <div className="panel-head">
              <div><h2>Top viewed products</h2><span>By unique visitors in the last 30 days</span></div>
            </div>
            {!insights.top_products?.length && <p className="empty-state">No product views in the window yet.</p>}
            <ul className="top-products-list">
              {insights.top_products?.map((p) => (
                <li key={p.product_id}>
                  {p.image && <img src={imageUrl(p.image)} alt="" />}
                  <span className="name">{p.name}</span>
                  <b>{p.unique_visitors}</b>
                </li>
              ))}
            </ul>
          </section>

          <section className="setting-card insights-abandoned" data-testid="insights-abandoned">
            <div className="panel-head">
              <div><h2>Abandoned carts ({abandonedCarts.length})</h2><span>Buyers who left before completing payment. Automatic reminder goes out after 2 hours.</span></div>
            </div>
            {!abandonedCarts.length && <p className="empty-state" data-testid="no-abandoned-carts">No abandoned carts yet — your buyers are checking out cleanly.</p>}
            {abandonedCarts.map((c) => (
              <div className="abandoned-row" key={c.id} data-testid={`abandoned-cart-${c.id}`}>
                <div className="abandoned-head">
                  <div>
                    <strong>{c.name || "Unnamed buyer"}</strong>
                    <span>{c.email}</span>
                    <small>{new Date(c.created_at).toLocaleString()}</small>
                  </div>
                  <div className="abandoned-stats">
                    <b>₹{Math.round(c.total)}</b>
                    {c.reminder_sent ? <small className="pill pill-sent">Reminder sent</small> : <small className="pill pill-pending">Reminder pending</small>}
                  </div>
                </div>
                <div className="abandoned-items">
                  {(c.items || []).map((it, idx) => (
                    <div className="abandoned-item" key={idx}>
                      <img src={imageUrl(it.image)} alt="" />
                      <span>{it.name}{it.variant_label ? ` · ${it.variant_label}` : ""} × {it.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="abandoned-actions">
                  <button className="pink-button ghost" onClick={() => resendAbandonedCart(c.id)} data-testid={`resend-cart-${c.id}`}><Mail size={13} /> Resend reminder</button>
                  <button className="pink-button ghost danger" onClick={() => deleteAbandonedCart(c.id)} data-testid={`delete-cart-${c.id}`}><Trash2 size={13} /> Remove</button>
                </div>
              </div>
            ))}
          </section>
        </div>
      )}

      <AIChat />
    </main>
  );
}

function AIChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useMemo(() => sessionStorage.getItem("shreya_ai_session") || crypto.randomUUID(), []);

  useEffect(() => { sessionStorage.setItem("shreya_ai_session", sessionId); }, [sessionId]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setHistoryLoading(true);
    axios.get(`${API}/ai/history`, { params: { session_id: sessionId } })
      .then((res) => { if (active) setMessages(res.data); })
      .catch(() => {})
      .finally(() => { if (active) setHistoryLoading(false); });
    return () => { active = false; };
  }, [open, sessionId]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const ask = async (text = input) => {
    const question = text.trim();
    if (!question || streaming || historyLoading) return;
    setInput("");
    setMessages((current) => [...current, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const response = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: question }),
      });
      if (!response.ok || !response.body) throw new Error("Assistant unavailable");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        chunks.forEach((chunk) => {
          const line = chunk.split("\n").find((part) => part.startsWith("data: "));
          if (!line) return;
          const data = JSON.parse(line.slice(6));
          if (data.type === "delta")
            setMessages((current) => {
              const next = [...current];
              const last = next[next.length - 1];
              if (!last || last.role !== "assistant") return next;
              next[next.length - 1] = { ...last, content: last.content + data.content };
              return next;
            });
          if (data.type === "error")
            setMessages((current) => {
              const next = [...current];
              const last = next[next.length - 1];
              if (!last) return next;
              next[next.length - 1] = { ...last, content: data.message };
              return next;
            });
        });
      }
    } catch {
      setMessages((current) => {
        const next = [...current];
        const last = next[next.length - 1];
        if (!last) return next;
        next[next.length - 1] = { ...last, content: "The assistant is unavailable right now. Please try again." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button className="ai-launcher" data-testid="ai-chat-launcher" onClick={() => setOpen(!open)} aria-label="Open Claude product assistant">
        <Sparkles size={17} /> <span>Ask Claude</span>
      </button>
      {open && (
        <section className="ai-panel" data-testid="ai-chat-panel">
          <div className="ai-panel-head">
            <div><strong>Claude product assistant</strong><span>Titles, descriptions & SEO copy</span></div>
            <button data-testid="ai-chat-close-button" onClick={() => setOpen(false)} aria-label="Close assistant"><X size={17} /></button>
          </div>
          <div className="ai-messages" data-testid="ai-chat-messages">
            {!messages.length && (
              <div className="ai-welcome">
                <MessageCircle size={25} />
                <strong>What are you listing today?</strong>
                <p>Tell me about a resin material and I’ll shape it into ready-to-publish product copy.</p>
                <div className="ai-suggestions">
                  <button disabled={historyLoading || streaming} data-testid="ai-suggestion-resin-button" onClick={() => ask("Create product copy for a 1 kg crystal clear epoxy resin kit priced at ₹599")}>Crystal clear resin</button>
                  <button disabled={historyLoading || streaming} data-testid="ai-suggestion-pigment-button" onClick={() => ask("Create product copy for a 12-colour pastel mica pigment set")}>Mica pigment set</button>
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div className={`ai-message ${message.role}`} data-testid={`ai-message-${message.role}-${index}`} key={`${index}-${message.role}`}>
                {message.content || (streaming && index === messages.length - 1 ? "Thinking…" : "")}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <form className="ai-form" onSubmit={(e) => { e.preventDefault(); ask(); }}>
            <input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Describe a product…" disabled={streaming || historyLoading} />
            <button data-testid="ai-chat-send-button" disabled={!input.trim() || streaming || historyLoading} aria-label="Send message"><Send size={16} /></button>
          </form>
        </section>
      )}
    </>
  );
}

export default App;
